package com.arenaproplay.admin.pages.analytics

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.arenaproplay.admin.domain.admin.*
import com.arenaproplay.admin.ui.components.admin.*
import com.arenaproplay.admin.ui.components.charts.svg.AreaChart
import com.arenaproplay.admin.ui.components.charts.svg.ChartAnnotation
import com.arenaproplay.admin.ui.components.charts.svg.ChartSeries
import com.arenaproplay.admin.ui.theme.AppColors
import com.arenaproplay.admin.ui.theme.bodySRegular
import com.arenaproplay.admin.ui.theme.eyebrow
import com.arenaproplay.admin.ui.theme.numHero
import com.arenaproplay.admin.ui.theme.numMedium
import kotlin.js.Date
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div

// ─────────────────────────────────────────────────────────────────────────────
// «Обзор» — данные бывшего «Командного центра» (/dashboard), вживлённые в
// вкладку «Источники и гео» на место карты мира: hero-метрика сессий с трендом
// и пиком, онлайн сейчас, выручка PRO и график активности (сессии + события,
// baseline-аннотация).
// ─────────────────────────────────────────────────────────────────────────────

private fun periodDurationRu(p: Period): String = when (p) {
    Period.DAY -> "за 24 часа"
    Period.WEEK -> "за 7 дней"
    Period.MONTH -> "за 30 дней"
    Period.YEAR -> "за год"
    Period.ALL -> "за всё время"
}

/** Label at the max point of the first series, or null if empty/all-zero. */
private fun derivePeakLabel(series: CategorizedTimeSeries?): String? {
    if (series == null) return null
    val values = series.series.values.firstOrNull()?.takeIf { it.isNotEmpty() } ?: return null
    val labels = series.labels.takeIf { it.isNotEmpty() } ?: return null
    val maxIdx = values.indices.maxByOrNull { values[it] } ?: return null
    if (values[maxIdx] <= 0.0) return null
    return labels.getOrNull(maxIdx)
}

/** Index of the analytics-baseline timestamp in the series labels, if visible. */
private fun baselineIndexFor(
    baselineTs: String?,
    series: CategorizedTimeSeries?,
    period: Period,
): Int? {
    val ts = baselineTs ?: return null
    val baselineMs = Date.parse(ts)
    if (baselineMs.isNaN()) return null
    val labels = series?.labels?.takeIf { it.isNotEmpty() } ?: return null
    val d = Date(baselineMs)
    fun pad2(n: Int): String = if (n < 10) "0$n" else n.toString()
    val labelToFind: String = when (period) {
        Period.DAY -> "${pad2(d.getHours())}:00"
        Period.YEAR -> "${d.getFullYear().toInt()}-${pad2(d.getMonth() + 1)}"
        else -> "${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}"
    }
    val idx = labels.indexOf(labelToFind)
    return if (idx == -1) null else idx
}

@Composable
fun AnalyticsOverviewBlock(
    behaviorKpis: BehaviorKpis?,
    subKpis: SubscriptionKpis?,
    onlineNow: OnlineNow?,
    activeUsersSeries: CategorizedTimeSeries?,
    eventsSeries: CategorizedTimeSeries?,
    baselineTs: String?,
    period: Period,
    kpisError: String?,
    chartError: String?,
) {
    Div(attrs = Modifier.fillMaxWidth().styleModifier {
            property("display", "grid")
            property("grid-template-columns", "minmax(0,1.6fr) minmax(0,1fr)")
            property("gap", "16px")
            property("align-items", "stretch")
        }.toAttrs()
    ) {
        // ── Left: hero sessions + activity chart ──────────────────────────────
        GlassCard(radius = 16, padding = 20) {
            Row(
                Modifier.fillMaxWidth()
                    .styleModifier {
                        property("justify-content", "space-between")
                        property("align-items", "flex-start")
                        property("gap", "12px")
                    }
            ) {
                Column(Modifier.styleModifier { property("min-width", "0") }) {
                    SpanText("АКТИВНОСТЬ", Modifier.eyebrow().color(AppColors.Faint))
                    Box(Modifier.height(6.px))
                    Row(
                        Modifier.styleModifier { property("align-items", "baseline"); property("gap", "12px") },
                        verticalAlignment = Alignment.Bottom,
                    ) {
                        val sessionsValue = behaviorKpis?.sessions?.current
                        SpanText(
                            if (sessionsValue != null) formatNumber(sessionsValue) else "—",
                            Modifier.numHero().color(AppColors.Ink)
                        )
                        if (behaviorKpis != null) {
                            TrendPill(
                                deltaPercent = behaviorKpis.sessions.percentDelta,
                                goodWhenUp = true,
                            )
                        }
                    }
                    Box(Modifier.height(3.px))
                    val peakLabel = derivePeakLabel(activeUsersSeries)
                    val subtitle = buildString {
                        append("сессий ${periodDurationRu(period)}")
                        if (peakLabel != null) append(" · пик — $peakLabel")
                    }
                    SpanText(
                        subtitle,
                        Modifier.bodySRegular()
                            .styleModifier { property("color", "rgba(240,244,252,.55)") }
                    )
                    if (kpisError != null) {
                        Box(Modifier.height(4.px))
                        SpanText(
                            "⚠ $kpisError",
                            Modifier.bodySRegular().styleModifier { property("color", "#FF6B5B") }
                        )
                    }
                }
                // Legend
                Row(
                    Modifier.styleModifier { property("gap", "12px"); property("align-items", "center"); property("flex-shrink", "0") }
                ) {
                    OverviewLegendItem("#4D8CFF", "сессии")
                    OverviewLegendItem("#00E5A4", "события")
                }
            }

            Box(Modifier.height(10.px))

            val baselineIndex = baselineIndexFor(baselineTs, activeUsersSeries, period)
            when {
                chartError != null -> OverviewChartPlaceholder("Ошибка загрузки графика")
                activeUsersSeries == null -> OverviewChartSkeleton()
                activeUsersSeries.labels.isEmpty() -> OverviewChartPlaceholder("Нет данных за период")
                else -> {
                    val seriesList = buildList {
                        val sessionVals = activeUsersSeries.series.values.firstOrNull() ?: emptyList()
                        add(ChartSeries("Сессии", sessionVals, "#4D8CFF"))
                        val evVals = eventsSeries?.series?.values?.firstOrNull()
                        if (evVals != null) add(ChartSeries("События", evVals, "#00E5A4"))
                    }
                    val annotations = buildList {
                        if (baselineIndex != null) {
                            add(ChartAnnotation(index = baselineIndex, text = "baseline", colorHex = "#E4CD40"))
                        }
                    }
                    AreaChart(
                        series = seriesList,
                        labels = activeUsersSeries.labels,
                        heightPx = 170,
                        annotations = annotations,
                        modifier = Modifier.fillMaxWidth(),
                    )
                    val labels = activeUsersSeries.labels
                    if (labels.size >= 2) {
                        Box(Modifier.height(4.px))
                        Row(
                            Modifier.fillMaxWidth()
                                .styleModifier { property("justify-content", "space-between") }
                        ) {
                            OverviewAxisLabel(labels.first())
                            if (baselineIndex != null) {
                                SpanText(
                                    "▪ baseline reset",
                                    Modifier.styleModifier {
                                        property("font-family", "'JetBrains Mono', monospace")
                                        property("font-size", "11px")
                                        property("color", "#E4CD40")
                                    }
                                )
                            }
                            OverviewAxisLabel(labels.last())
                        }
                    }
                }
            }
        }

        // ── Right: online now + PRO revenue ───────────────────────────────────
        Column(
            Modifier.styleModifier {
                property("display", "flex")
                property("flex-direction", "column")
                property("gap", "16px")
                property("min-width", "0")
            }
        ) {
            GlassCard(radius = 16, padding = 20) {
                SpanText("ОНЛАЙН СЕЙЧАС", Modifier.eyebrow().color(AppColors.Faint))
                Box(Modifier.height(6.px))
                Row(
                    Modifier.styleModifier { property("align-items", "center"); property("gap", "10px") }
                ) {
                    Box(
                        Modifier.width(8.px).height(8.px).borderRadius(50.percent)
                            .styleModifier {
                                property("background", "#00E5A4")
                                property("box-shadow", "0 0 12px #00E5A4")
                                property("flex-shrink", "0")
                            }
                    )
                    SpanText(
                        onlineNow?.online?.let { formatNumber(it.toLong()) } ?: "—",
                        Modifier.numMedium().color(AppColors.Ink)
                    )
                    SpanText(
                        "юзеров в приложении",
                        Modifier.bodySRegular()
                            .styleModifier { property("color", "rgba(240,244,252,.45)") }
                    )
                }
            }

            GlassCard(radius = 16, padding = 20) {
                SpanText("ВЫРУЧКА PRO", Modifier.eyebrow().color(AppColors.Faint))
                Box(Modifier.height(6.px))
                when {
                    kpisError != null -> SpanText(
                        "Ошибка",
                        Modifier.bodySRegular().styleModifier { property("color", "#FF6B5B") }
                    )
                    subKpis == null -> Box(
                        Modifier.fillMaxWidth().height(24.px)
                            .styleModifier {
                                property("background", "rgba(255,255,255,.04)")
                                property("border-radius", "6px")
                            }
                    )
                    else -> Row(
                        Modifier.styleModifier { property("align-items", "baseline"); property("gap", "8px") }
                    ) {
                        val currency = subKpis.currency
                        val revenueStr = when (currency.uppercase()) {
                            "UAH" -> "₴${formatNumber(subKpis.revenue.current)}"
                            "USD" -> "\$${formatNumber(subKpis.revenue.current)}"
                            "EUR" -> "€${formatNumber(subKpis.revenue.current)}"
                            else -> "${formatNumber(subKpis.revenue.current)} $currency"
                        }
                        SpanText(revenueStr, Modifier.numMedium().color(AppColors.Ink))
                        val delta = subKpis.revenue.percentDelta
                        if (delta != null) {
                            TrendPill(deltaPercent = delta, goodWhenUp = true)
                        }
                    }
                }
                if (subKpis != null) {
                    Box(Modifier.height(3.px))
                    SpanText(
                        periodDurationRu(period),
                        Modifier.styleModifier {
                            property("font-size", "11px")
                            property("color", "rgba(240,244,252,.45)")
                        }
                    )
                }
            }
        }
    }
}

// ── Small pieces ─────────────────────────────────────────────────────────────

@Composable
private fun OverviewLegendItem(colorHex: String, label: String) {
    Row(
        Modifier.styleModifier { property("align-items", "center"); property("gap", "5px") }
    ) {
        Box(
            Modifier.width(7.px).height(7.px).borderRadius(50.percent)
                .styleModifier { property("background", colorHex) }
        )
        SpanText(
            label,
            Modifier.bodySRegular()
                .styleModifier { property("color", "rgba(240,244,252,.45)") }
        )
    }
}

@Composable
private fun OverviewAxisLabel(text: String) {
    SpanText(
        text,
        Modifier.styleModifier {
            property("font-family", "'JetBrains Mono', monospace")
            property("font-size", "11px")
            property("color", "rgba(240,244,252,.45)")
        }
    )
}

@Composable
private fun OverviewChartPlaceholder(text: String) {
    Box(
        Modifier.fillMaxWidth().height(170.px),
        contentAlignment = Alignment.Center,
    ) {
        SpanText(text, Modifier.bodySRegular().color(AppColors.Faint))
    }
}

@Composable
private fun OverviewChartSkeleton() {
    Box(
        Modifier.fillMaxWidth().height(170.px)
            .styleModifier {
                property("background", "rgba(255,255,255,.02)")
                property("border-radius", "8px")
            }
    )
}
